import { Component, OnInit, Input} from "@angular/core";
import { DatePipe } from "@angular/common";
import { Router, NavigationStart } from '@angular/router';
import { Observable, EMPTY, of, Subscription } from 'rxjs';
import { concatMap, tap } from "rxjs/operators";
import { filter, finalize, map } from "rxjs/operators";
//import { GridOptions, ColDef, AllModules } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";


import { dateComparator } from "../../../common/components/data-table/comparators/data-table.comparators";
import { RouterLinkRendererComponent } from "../../../common/components/ag-grid/router-link-renderer.component";
import { ModalInfoComponent } from "../../../common/components/ag-grid/modal-info.component";
import { TabComponent } from "../../../common/components/tabs/tab.component";

import { AuthService } from "../../../services/auth.service";
import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { WorkOrderService } from "../../../common/services/work-order.service";

import { UserIdentity } from "../../../models/user-identity.model";
import { WorkOrderStatus, WorkOrderType, WorkOrderWithAssignments} from "../../../common/models/work-order.model";
import { Contact, ContactStub } from "../../../admin/models/contact.model";
import { GroupSearchSpec, GroupService } from "../../../admin/services/group.service";
import { ContactSearchSpec, ContactService } from "../../../admin/services/contacts.service";

@Component({
    selector: "scanning-widget",
    templateUrl: "./scanning-widget.component.html",
    standalone: false
})
export class ScanningWidgetComponent implements OnInit {
    @Input() widgetTitle: string;

    awaitingConfirmationData: Observable<WorkOrderWithAssignments[]> = of(null);
	awaitingConfirmationGridOptions: GridOptions;
	awaitingConfirmationGridApi?: GridApi;
    awaitingLegalData: Observable<WorkOrderWithAssignments[]> = of(null);
	awaitingLegalGridOptions: GridOptions;
	awaitingLegalGridApi?: GridApi;


    awaitingScanData: Observable<WorkOrderWithAssignments[]> = of(null);
	awaitingScanGridOptions: GridOptions;
	awaitingScanGridApi?: GridApi;
    allContacts: ContactStub[] = [];

    subscription: Subscription = new Subscription();




    get user(): UserIdentity {
        return this.authService.User;
    }

    constructor(
        public readonly _service: WorkOrderService,
        public readonly authService: AuthService,
        public readonly datePipe:  DatePipe,
        private formatterService: GridFormatterService,
        private router: Router,
        public contactService: ContactService

	) { }

    ngOnInit() {


        let contactSearch$: Observable<ContactStub[]> = this.contactService.getAll({stubs: false} as ContactSearchSpec).pipe(tap((contacts) => {
            this.allContacts = contacts;
        }));

        let sub$ = contactSearch$.pipe().subscribe();

        this.subscription.add(sub$);
        this.awaitingScanGridOptions = {
            columnDefs: this.createColumnDefinitions(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.awaitingScanGridApi = params.api;
                this.awaitingScanGridApi.sizeColumnsToFit();
            },
            pagination: true,
            paginationAutoPageSize: true,
            suppressHorizontalScroll: true,
            headerHeight: 50
        } as GridOptions;

        this.awaitingConfirmationGridOptions = {
            columnDefs: this.createColumnDefinitions(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.awaitingConfirmationGridApi = params.api;
                this.awaitingConfirmationGridApi.sizeColumnsToFit();
            },
            pagination: true, 
            paginationAutoPageSize: true,
            suppressHorizontalScroll: false,
            headerHeight: 50
        } as GridOptions;

        this.awaitingLegalGridOptions = {
            columnDefs: this.createColumnDefinitions(),
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.awaitingLegalGridApi = params.api;
                this.awaitingLegalGridApi.sizeColumnsToFit();
            },
            pagination: true,
            paginationAutoPageSize: true,
            suppressHorizontalScroll: false,
            headerHeight: 50
        } as GridOptions;

        this.loadData("Awaiting Scan");

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.awaitingScanData = EMPTY;
			this.awaitingLegalData = EMPTY;
			this.awaitingConfirmationData = EMPTY;
        });

    }

    public createColumnDefinitions() {
        let colDefs = [
            {
                headerClass: "wrapped",
                headerName: "Date Submitted",
                headerTooltip: "Date Submitted",
                field: "creationDate",
                tooltipValueGetter: this.formatterService.formatDate("creationDate"),
                valueGetter: this.formatterService.formatDate("creationDate"),
                comparator: dateComparator,
                minWidth: 150,
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Application #",
                headerTooltip: "Application #",
                cellRendererSelector: (params) => {
                    if (this.authService.User.groups.find(g => g === params.data.assignedGroupId) || this.authService.User.id === params.data.assignedUserId) {
                        return {
                            component: RouterLinkRendererComponent,
                            params: {
                                openInNewWindow: false,
                                textField: "applicationNumber",
                                url: (data: WorkOrderWithAssignments) => {
                                    return data.targetUrl;
                                }
                            }
                        };
                    } else {
                        return {
                            component: ModalInfoComponent,
                            params: {
                                modalText: (data) => { return 'You do not have permission to access this request.' },
                                text: (data) => { return data.applicationNumber; }
                            }
                        };
                    }
                },
                field: "applicationNumber",
                tooltipField: "applicationNumber",
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Organization",
                headerTooltip: "Organization",
                field: "companyName",
                tooltipField: "companyName",
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Application Type",
                headerTooltip: "Application Type",
                field: "description",
                tooltipField: "description",
            },
            {
                headerClass: "wrapped",
                headerName: "Initiator",
                headerTooltip: "Initiator",
                field: "creatorId",
                valueFormatter: this.contactFormatter("creatorId"),
                tooltipValueGetter: this.contactFormatter("creatorId"),
                minWidth: 100
            }


        ] as ColDef[];
        this.formatterService.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    loadData(tab: string): void {
        switch (tab) {
            case "Awaiting Scan":
                if (this.awaitingScanGridApi) {
                    this.awaitingScanGridApi.setGridOption("loading", true);
                }
                this.awaitingScanData = this._service.getWorkOrdersByStatusAndType(WorkOrderStatus.Pending, WorkOrderType.ScanDocuments).pipe(
                    concatMap((orders: WorkOrderWithAssignments[]) => {
                        let sorted = orders.filter(o => o.assignedGroupId != '-1').sort((a, b) => {
                            if (a.creationDate < b.creationDate) return 1;
                            if (b.creationDate < a.creationDate) return -1;
                            return 0;
                        });
                        return of(sorted);
                    }),
                    finalize(() => this.awaitingScanGridApi?.setGridOption("loading", false))
                );
                this.awaitingConfirmationData = of(null);
                this.awaitingLegalData = of(null);
                break;
            case "Awaiting Confirmation":
                if (this.awaitingConfirmationGridApi) {
                    this.awaitingConfirmationGridApi.setGridOption("loading", true);
                }
                this.awaitingConfirmationData = this._service.getWorkOrdersByStatusAndType(WorkOrderStatus.Pending, WorkOrderType.ReviewScannedDocuments).pipe(
                    concatMap((orders: WorkOrderWithAssignments[]) => {
                        let sorted = orders.filter(o => o.assignedGroupId != '-1').sort((a, b) => {
                            if (a.creationDate < b.creationDate) return 1;
                            if (b.creationDate < a.creationDate) return -1;
                            return 0;
                        });
                        return of(sorted);
                    }),
                    finalize(() => this.awaitingConfirmationGridApi?.setGridOption("loading", false))
                );
                this.awaitingScanData = of(null);
                this.awaitingLegalData = of(null);
                break;
            case "Awaiting Legal Review":
                if (this.awaitingLegalGridApi) {
                    this.awaitingLegalGridApi.setGridOption("loading", true);
                }
                this.awaitingLegalData = this._service.getWorkOrdersByStatusAndType(WorkOrderStatus.Pending, WorkOrderType.LegalReviewScannedDocuments).pipe(
                    concatMap((orders: WorkOrderWithAssignments[]) => {
                        let sorted = orders.filter(o => o.assignedGroupId != '-1').sort((a, b) => {
                            if (a.creationDate < b.creationDate) return 1;
                            if (b.creationDate < a.creationDate) return -1;
                            return 0;
                        });
                        return of(sorted);
                    }),
                    finalize(() => this.awaitingLegalGridApi?.setGridOption("loading", false))
                );
                this.awaitingScanData = of(null);
                this.awaitingConfirmationData = of(null);
                break;
            default:
                this.awaitingScanData = of(null);
                this.awaitingConfirmationData = of(null);
                this.awaitingLegalData = of(null);
                break;
        }
        
    }

    contactFormatter(field: any): any {
        return (obj) => {
   
            let name: string = "N/A";
            if (obj != null && obj.value != null) {
                let match = this.allContacts.find(c => c.id === obj.value);

                if (match != null) name = match.displayName;
                //else return obj.value;
            }
            return name;
        };
    }

    onTabClicked(component: TabComponent): void {
        this.loadData(component.title);
    }
}
